/**
 * CR150: UX Hash Utilities for Layout Studio
 * 
 * Generates stable short hashes from layout/UX config objects for
 * "provable" debug output and cache-busting.
 */

import type { LayoutJson } from "./types";
import type { DealCardUXConfig } from "./dealCardUXTypes";

/**
 * Compute a stable 8-character hash from any object.
 * Uses djb2 algorithm for synchronous, deterministic hashing.
 */
export function computeUxHash(obj: unknown): string {
  try {
    const str = JSON.stringify(obj, Object.keys(obj as object).sort());
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash) + str.charCodeAt(i);
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).padStart(8, '0').slice(0, 8);
  } catch {
    return "00000000";
  }
}

/**
 * Compute uxHash specifically from a LayoutJson object.
 * Includes dealCardUX, typography, blocks, filters, etc.
 */
export function computeLayoutUxHash(layout: LayoutJson): string {
  // Extract the key config that affects visual rendering
  const hashInput = {
    dealCardUX: layout.dealCardUX,
    typography: layout.typography,
    blocks: layout.blocks.map(b => ({ id: b.id, enabled: b.enabled, order: b.order })),
    filters: layout.filters.map(f => ({ id: f.id, enabled: f.enabled, order: f.order })),
    sortOptions: layout.sortOptions.map(s => ({ id: s.id, enabled: s.enabled })),
    defaultSort: layout.defaultSort,
    resultsConfig: layout.resultsConfig,
  };
  return computeUxHash(hashInput);
}

/**
 * Compute uxHash from a DealCardUXConfig specifically.
 */
export function computeDealCardUxHash(config: DealCardUXConfig | undefined): string {
  if (!config) return "default0";
  return computeUxHash(config);
}
